import React from "react";
import Seo from "@/components/Seo";
import { Container, Overline } from "@/components/ui-kit";
import { COMPANY } from "@/lib/content";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    seo: "Privacy Policy for Redwood Capital Holdings, Inc.",
    body: [
      ["Overview", `${COMPANY.name} ("Redwood Capital," "we," or "us") respects your privacy. This Privacy Policy describes how we collect, use, and protect information you provide through this website.`],
      ["Information We Collect", "We collect information you voluntarily submit through our forms, such as your name, email, phone number, company, and details about properties you submit. We may also collect standard technical information such as browser type and pages visited."],
      ["How We Use Information", "We use the information you provide to evaluate property opportunities, respond to inquiries, communicate with you, and operate our business. We do not sell your personal information."],
      ["File Uploads", "Documents you upload (such as surveys, site plans, or offering memoranda) are stored securely and used solely to evaluate the associated opportunity."],
      ["Data Retention & Security", "We retain submitted information for as long as needed to fulfill the purposes described here and take reasonable measures to protect it. No method of transmission over the Internet is fully secure."],
      ["Contact", `For privacy questions, contact us at ${COMPANY.email}.`],
    ],
  },
  terms: {
    title: "Terms of Use",
    seo: "Terms of Use for the Redwood Capital Holdings website.",
    body: [
      ["Acceptance of Terms", `By accessing this website, you agree to these Terms of Use. If you do not agree, please do not use the site.`],
      ["Use of the Website", "This website is provided for general informational purposes. You agree not to misuse the site or submit false, misleading, or unlawful information."],
      ["No Professional Advice", "Content on this website does not constitute legal, tax, financial, or investment advice. You should consult your own advisors before making decisions."],
      ["Intellectual Property", `All content, branding, and materials on this website are the property of ${COMPANY.name} unless otherwise noted and may not be reproduced without permission.`],
      ["Limitation of Liability", "The website is provided \u201cas is\u201d without warranties of any kind. We are not liable for any damages arising from your use of the site."],
      ["Changes", "We may update these terms at any time. Continued use of the site constitutes acceptance of the revised terms."],
    ],
  },
  disclaimer: {
    title: "Investment Disclaimer",
    seo: "Investment disclaimer for Redwood Capital Holdings, Inc.",
    body: [
      ["Informational Purposes Only", "The information contained on this website is provided for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to purchase any security or investment product."],
      ["No Offer or Solicitation", "Any offer or solicitation will be made only through definitive offering documents and in accordance with applicable securities laws. Nothing on this website should be relied upon as a promise or representation regarding future results."],
      ["Risk of Loss", "Investments in real estate involve substantial risk, including the potential loss of principal. Past performance is not indicative of future results."],
      ["No Advice", "Nothing on this website constitutes investment, legal, tax, or financial advice. Prospective investors should consult their own advisors and conduct independent due diligence before making any investment decision."],
      ["Forward-Looking Statements", "Statements regarding potential opportunities, strategy, or outcomes are forward-looking and subject to risks and uncertainties. Actual results may differ materially."],
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind];
  return (
    <div data-testid={`legal-${kind}-page`}>
      <Seo title={c.title} description={c.seo} path={`/${kind}`} />
      <section className="bg-forest text-white py-20 relative overflow-hidden">
        <div className="grain" />
        <Container className="relative">
          <Overline>Legal</Overline>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold tracking-tight">{c.title}</h1>
        </Container>
      </section>
      <section className="py-16 md:py-24">
        <Container>
          <div className="max-w-3xl space-y-10">
            {c.body.map(([h, p]) => (
              <div key={h}>
                <h2 className="font-serif text-xl font-semibold text-forest">{h}</h2>
                <p className="mt-3 text-ink leading-relaxed">{p}</p>
              </div>
            ))}
            <p className="text-ink/60 text-sm pt-6 border-t border-line">Last updated {new Date().getFullYear()}. {COMPANY.name}, {COMPANY.location}.</p>
          </div>
        </Container>
      </section>
    </div>
  );
}
